import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, Euro, ShoppingBag, TrendingUp, Percent, PackageX,
  ChevronRight, RefreshCw, PackageCheck, Undo2, User, Mail, Phone, MapPin, Clock, X, Info,
  Truck, Download, Printer, ExternalLink, PackagePlus,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  apiCommerceKpis, apiCommerceOrders, apiCommerceOrder, apiFulfillOrder,
  apiRefundOrder, apiUpdateOrderNotes, apiReconcileOrder, eur, errMsg, GRADE_LABELS,
  apiCreateShipment, apiGetShipment, apiBulkShipments, apiUpdateOrderShippingCost,
} from "../adminApi";

// Converte una stringa base64 (PDF) in un Blob scaricabile/stampabile.
function b64ToBlob(b64, type = "application/pdf") {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type });
}
function downloadPdf(b64, filename) {
  const url = URL.createObjectURL(b64ToBlob(b64));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
function printPdf(b64) {
  const url = URL.createObjectURL(b64ToBlob(b64));
  const w = window.open(url);
  if (w) w.addEventListener("load", () => { try { w.print(); } catch { /* noop */ } });
}

const PAY = {
  paid: { label: "Pagato", cls: "bg-lime text-ink" },
  pending: { label: "In attesa", cls: "bg-surface3 text-steel border border-line" },
  failed: { label: "Fallito", cls: "bg-destructive text-destructive-foreground" },
  refunded: { label: "Rimborsato", cls: "bg-surface3 text-chalk border border-line" },
  mismatch: { label: "Discrepanza", cls: "bg-carrot/15 text-carrot border border-carrot/40" },
};
const FUL = {
  unfulfilled: { label: "Da evadere", cls: "bg-surface3 text-steel border border-line" },
  fulfilled: { label: "Evaso", cls: "bg-lime/15 text-lime border border-lime/40" },
};

function Pill({ map, value, testid }) {
  const it = map[value] || { label: value || "—", cls: "bg-surface3 text-steel border border-line" };
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${it.cls}`}
      data-testid={testid}
    >
      {it.label}
    </span>
  );
}

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function Kpi({ label, value, icon: Icon, testid, loading }) {
  return (
    <Card className="border-line bg-surface2" data-testid={testid}>
      <CardContent className="flex items-start justify-between gap-2 p-4">
        <div className="min-w-0">
          <p className="text-xs font-medium text-steel">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-7 w-20" />
          ) : (
            <p className="mt-1 truncate font-display text-2xl font-bold tabular-nums text-chalk">{value}</p>
          )}
        </div>
        <Icon className="h-5 w-5 shrink-0 text-tertiary" />
      </CardContent>
    </Card>
  );
}

// -------------------- Dettaglio ordine (Sheet) --------------------
const EVENT_LABELS = {
  "payment.succeeded": "Pagamento riuscito",
  "payment.failed": "Pagamento fallito",
  "payment.pending": "Pagamento in attesa",
  "refund.created": "Rimborso creato",
};

function OrderDetail({ orderNumber, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [shipBusy, setShipBusy] = useState(false);
  const [shipCost, setShipCost] = useState("");
  const [savingCost, setSavingCost] = useState(false);

  const load = useCallback(() => {
    if (!orderNumber) return;
    setLoading(true);
    apiCommerceOrder(orderNumber)
      .then((d) => {
        setData(d);
        setNotes(d.order.admin_notes || "");
        const c = d.order.costo_spedizione_reale_cents;
        setShipCost(c != null ? String(c / 100) : "");
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  useEffect(load, [load]);

  const loadShipment = useCallback(() => {
    if (!orderNumber) return;
    apiGetShipment(orderNumber)
      .then((d) => setShipment(d.shipment))
      .catch(() => setShipment(null));
  }, [orderNumber]);
  useEffect(loadShipment, [loadShipment]);

  const doCreateShipment = async () => {
    setShipBusy(true);
    try {
      const res = await apiCreateShipment(orderNumber);
      if (res.ok) {
        toast.success("Spedizione GLS creata");
        loadShipment();
        load();
        onChanged?.();
      } else {
        toast.error(res.error || "Creazione spedizione non riuscita");
        loadShipment();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setShipBusy(false);
    }
  };

  const order = data?.order;
  const events = data?.webhook_events || [];
  const isShipped = order?.shipping_status === "spedito" || shipment?.status === "spedito";
  const canCreateShipment = order?.payment_status === "paid" && !isShipped;
  const shipmentError = shipment?.status === "error" ? shipment?.error_message : null;

  const timeline = useMemo(() => {
    if (!order) return [];
    const t = [{ ts: order.created_at, label: "Ordine creato" }];
    if (order.sumup_checkout_id) t.push({ ts: order.created_at, label: "Checkout avviato" });
    for (const e of events) t.push({ ts: e.received_at, label: EVENT_LABELS[e.type] || e.type });
    if (order.refunded_at) t.push({ ts: order.refunded_at, label: "Rimborsato" });
    if (order.fulfilled_at) t.push({ ts: order.fulfilled_at, label: "Evaso" });
    return t.filter((x) => x.ts).sort((a, b) => new Date(a.ts) - new Date(b.ts));
  }, [order, events]);

  const doFulfill = async () => {
    setBusy(true);
    try {
      await apiFulfillOrder(orderNumber, order.fulfillment_status !== "fulfilled");
      toast.success(order.fulfillment_status !== "fulfilled" ? "Ordine segnato come evaso" : "Ordine riportato da evadere");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doRefund = async () => {
    setBusy(true);
    try {
      await apiRefundOrder(orderNumber);
      toast.success("Ordine segnato come rimborsato");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doReconcile = async () => {
    setBusy(true);
    try {
      const res = await apiReconcileOrder(orderNumber);
      if (res.result === "reconciled") toast.success("Ordine riconciliato e segnato come Pagato");
      else if (res.result === "already_paid") toast.success("Ordine già pagato");
      else if (res.result === "no_success_event") toast.error("Nessun pagamento riuscito registrato per questo ordine");
      else toast.error("Impossibile riconciliare");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await apiUpdateOrderNotes(orderNumber, notes);
      toast.success("Note salvate");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingNotes(false);
    }
  };

  const saveShippingCost = async () => {
    setSavingCost(true);
    try {
      const cents = shipCost === "" ? 0 : Math.round(Number(shipCost) * 100);
      const res = await apiUpdateOrderShippingCost(orderNumber, cents, "manuale");
      toast.success("Costo spedizione aggiornato");
      setData((d) => (d ? { ...d, order: { ...d.order,
        costo_spedizione_reale_cents: res.costo_spedizione_reale_cents,
        costo_spedizione_fonte: res.costo_spedizione_fonte } } : d));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingCost(false);
    }
  };

  return (
    <SheetContent
      side="right"
      className="w-full gap-0 overflow-hidden border-line bg-ink p-0 sm:max-w-xl"
      data-testid="admin-order-detail-panel"
    >
      <div className="flex h-14 items-center justify-between border-b border-line px-5">
        <div className="min-w-0">
          <p className="truncate font-display text-base font-bold text-chalk">{orderNumber}</p>
          {order && <p className="text-xs text-steel">{fmtDateTime(order.created_at)}</p>}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Chiudi" data-testid="order-detail-close">
          <X className="h-5 w-5" />
        </Button>
      </div>

      <ScrollArea className="h-[calc(100vh-3.5rem)]">
        <div className="flex flex-col gap-5 p-5">
          {loading || !order ? (
            <>
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-40 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
            </>
          ) : (
            <>
              {/* Stato + azioni */}
              <Card className="border-line bg-surface2">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill map={PAY} value={order.payment_status} testid="order-detail-payment-status" />
                    <Pill map={FUL} value={order.fulfillment_status} testid="order-detail-fulfillment-status" />
                  </div>
                  <div className="flex gap-2">
                    {order.payment_status !== "paid" && order.payment_status !== "refunded" && (
                      <Button
                        size="sm"
                        variant="lime"
                        disabled={busy}
                        onClick={doReconcile}
                        data-testid="order-reconcile-button"
                      >
                        <RefreshCw className="h-4 w-4" /> Riconcilia
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={order.fulfillment_status === "fulfilled" ? "secondary" : "lime"}
                      disabled={busy}
                      onClick={doFulfill}
                      data-testid="order-fulfill-button"
                    >
                      <PackageCheck className="h-4 w-4" />
                      {order.fulfillment_status === "fulfilled" ? "Da evadere" : "Segna evaso"}
                    </Button>
                    {order.payment_status === "paid" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="secondary" disabled={busy} data-testid="order-refund-button">
                            <Undo2 className="h-4 w-4" /> Rimborsa
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Segnare come rimborsato?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Questo aggiorna solo lo stato interno. Il rimborso va effettuato dal pannello SumUp.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={doRefund} data-testid="order-refund-confirm">Conferma</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>

              {order.payment_review && order.review_note && (
                <div className="flex items-start gap-2 rounded-lg border border-carrot/40 bg-carrot/10 p-3" data-testid="order-review-banner">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-carrot" />
                  <p className="text-sm text-steel">Da verificare: {order.review_note}</p>
                </div>
              )}

              {/* Articoli */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Articoli</p>
                <Card className="border-line bg-surface2">
                  <CardContent className="divide-y divide-line p-0">
                    {order.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="order-detail-item">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-chalk">{it.name}</p>
                          <p className="text-xs text-steel">
                            {it.brand} · {GRADE_LABELS[it.grade] || it.grade} · ×{it.quantity}
                          </p>
                        </div>
                        <span className="shrink-0 text-sm tabular-nums text-chalk">{eur(it.unit_price * it.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-steel">Subtotale</span>
                      <span className="tabular-nums text-chalk">{eur(order.subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-steel">Spedizione</span>
                      <span className="tabular-nums text-chalk">{order.shipping_cost ? eur(order.shipping_cost) : "Gratuita"}</span>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-semibold text-chalk">Totale</span>
                      <span className="font-display text-lg font-bold tabular-nums text-chalk">{eur(order.total)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cliente */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Cliente</p>
                <Card className="border-line bg-surface2">
                  <CardContent className="flex flex-col gap-2 p-4 text-sm text-chalk">
                    <span className="flex items-center gap-2"><User className="h-4 w-4 text-tertiary" /> {order.customer.full_name}</span>
                    <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-tertiary" /> {order.customer.email}</span>
                    <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-tertiary" /> {order.customer.phone}</span>
                    <span className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-tertiary" />
                      <span>{order.customer.address}, {order.customer.zip_code} {order.customer.city} {order.customer.province && `(${order.customer.province})`}</span>
                    </span>
                    {order.customer.notes && <p className="mt-1 text-steel">Nota cliente: {order.customer.notes}</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Spedizione GLS */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Spedizione</p>
                <Card className="border-line bg-surface2">
                  <CardContent className="flex flex-col gap-3 p-4" data-testid="order-shipment-section">
                    {isShipped ? (
                      <>
                        <div className="flex items-center gap-2 text-sm text-chalk">
                          <Truck className="h-4 w-4 text-lime" />
                          <span>Spedito con <strong>{shipment?.carrier || order.carrier || "GLS"}</strong></span>
                        </div>
                        <div className="flex items-center justify-between gap-2 rounded-lg bg-surface3 px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-xs text-steel">Numero di tracking</p>
                            <p className="truncate text-sm font-medium text-chalk" data-testid="order-tracking-number">
                              {shipment?.tracking_number || order.tracking_number || "—"}
                            </p>
                          </div>
                          {(shipment?.tracking_url || order.tracking_url) && (
                            <a
                              href={shipment?.tracking_url || order.tracking_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid="order-tracking-link"
                            >
                              <Button size="sm" variant="secondary"><ExternalLink className="h-4 w-4" /> Traccia</Button>
                            </a>
                          )}
                        </div>
                        {shipment?.has_label && shipment?.label_pdf ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="lime"
                              onClick={() => downloadPdf(shipment.label_pdf, `etichetta-${orderNumber}.pdf`)}
                              data-testid="order-label-download"
                            >
                              <Download className="h-4 w-4" /> Scarica etichetta
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => printPdf(shipment.label_pdf)}
                              data-testid="order-label-print"
                            >
                              <Printer className="h-4 w-4" /> Stampa
                            </Button>
                          </div>
                        ) : (
                          <p className="text-xs text-steel">Etichetta non disponibile per questa spedizione.</p>
                        )}
                      </>
                    ) : canCreateShipment ? (
                      <>
                        <p className="text-sm text-steel">
                          Crea la spedizione GLS: genereremo tracking ed etichetta e avviseremo il cliente via email.
                        </p>
                        {shipmentError && (
                          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                            <p className="text-sm text-steel" data-testid="order-shipment-error">
                              Ultimo tentativo non riuscito: {shipmentError}
                            </p>
                          </div>
                        )}
                        <div>
                          <Button
                            size="sm"
                            variant="lime"
                            disabled={shipBusy}
                            onClick={doCreateShipment}
                            data-testid="order-create-shipment"
                          >
                            <PackagePlus className="h-4 w-4" /> {shipBusy ? "Creazione…" : "Crea spedizione GLS"}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-steel">La spedizione sarà disponibile dopo il pagamento dell'ordine.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Costo reale spedizione (interno, per Finance) */}
              {(order.payment_status === "paid" || order.payment_status === "refunded") && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Costo reale spedizione</p>
                  <Card className="border-line bg-surface2">
                    <CardContent className="flex flex-col gap-2 p-4" data-testid="order-shipping-cost-section">
                      <div className="flex items-end gap-2">
                        <div className="flex flex-col gap-1">
                          <label htmlFor="ship-cost" className="text-xs text-steel">Costo sostenuto (€)</label>
                          <Input
                            id="ship-cost"
                            type="number"
                            step="0.01"
                            value={shipCost}
                            placeholder="es. 6.00"
                            onChange={(e) => setShipCost(e.target.value)}
                            className="h-9 w-32 bg-surface"
                            data-testid="order-shipping-cost-input"
                          />
                        </div>
                        <Button size="sm" variant="lime" onClick={saveShippingCost} disabled={savingCost} data-testid="order-shipping-cost-save">
                          Salva
                        </Button>
                      </div>
                      <p className="text-xs text-tertiary">
                        Fonte attuale: <strong>{order.costo_spedizione_fonte || "non impostata"}</strong>
                        {" "}· il salvataggio manuale ha la precedenza su default e GLS.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Timeline */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Cronologia</p>
                <Card className="border-line bg-surface2">
                  <CardContent className="flex flex-col gap-3 p-4">
                    {timeline.map((t, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <Clock className="h-4 w-4 shrink-0 text-tertiary" />
                        <span className="text-chalk">{t.label}</span>
                        <span className="ml-auto text-xs text-steel">{fmtDateTime(t.ts)}</span>
                      </div>
                    ))}
                    {order.failure_message && (
                      <p className="text-xs text-destructive">Motivo: {order.failure_message}</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Note interne */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel">Note interne</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Visibili solo al team"
                  rows={3}
                  className="bg-surface2"
                  data-testid="order-notes-textarea"
                />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="lime" onClick={saveNotes} disabled={savingNotes} data-testid="order-notes-save">
                    Salva note
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </SheetContent>
  );
}

// -------------------- Pagina Ordini --------------------
export default function Orders() {
  const [kpis, setKpis] = useState(null);
  const [tab, setTab] = useState("orders");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [combinedPdf, setCombinedPdf] = useState(null);

  const loadKpis = useCallback(() => {
    apiCommerceKpis(30).then(setKpis).catch(() => setKpis(null));
  }, []);
  useEffect(loadKpis, [loadKpis]);

  const loadList = useCallback(() => {
    setError("");
    setList(null);
    setSelectedIds([]);
    const params = { scope: tab, page: 1, page_size: 50 };
    if (search.trim()) params.q = search.trim();
    if (status !== "all") params.status = status;
    apiCommerceOrders(params)
      .then(setList)
      .catch((e) => setError(errMsg(e)));
  }, [tab, search, status]);
  useEffect(loadList, [loadList]);

  // Debounce ricerca
  useEffect(() => {
    const t = setTimeout(() => setSearch(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const onTab = (v) => {
    setTab(v);
    setStatus("all");
  };

  const statusOptions =
    tab === "orders"
      ? [["all", "Tutti"], ["paid", "Pagato"], ["refunded", "Rimborsato"], ["mismatch", "Discrepanza"]]
      : [["all", "Tutti"], ["pending", "In attesa"], ["failed", "Fallito"]];

  const refreshAll = () => { loadKpis(); loadList(); };

  const paidRows = (list?.items || []).filter((o) => o.payment_status === "paid");
  const allSelected = paidRows.length > 0 && selectedIds.length === paidRows.length;
  const toggleOne = (on) =>
    setSelectedIds((s) => (s.includes(on) ? s.filter((x) => x !== on) : [...s, on]));
  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : paidRows.map((o) => o.order_number));
  const runBulk = async () => {
    if (selectedIds.length === 0) return;
    setBulkRunning(true);
    try {
      const res = await apiBulkShipments(selectedIds);
      setBulkResults(res);
      setCombinedPdf(res.combined_label_pdf || null);
      if (res.ok_count > 0) toast.success(`${res.ok_count} spedizioni create`);
      if (res.ok_count < res.total) toast.warning(`${res.total - res.ok_count} ordini non spediti`);
      loadList();
      loadKpis();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Ordini</h1>
          <p className="mt-1 text-sm text-steel">Vendite, pagamenti e carrelli abbandonati (ultimi 30 giorni).</p>
        </div>
        <Button variant="secondary" size="sm" onClick={refreshAll} data-testid="orders-refresh">
          <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Aggiorna</span>
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Incassato" value={eur(kpis?.revenue)} icon={Euro} loading={!kpis} testid="kpi-revenue" />
        <Kpi label="Ordini" value={kpis?.orders ?? 0} icon={ShoppingBag} loading={!kpis} testid="kpi-orders" />
        <Kpi label="Valore medio" value={eur(kpis?.aov)} icon={TrendingUp} loading={!kpis} testid="kpi-aov" />
        <Kpi label="Conversione" value={`${kpis?.conversion_rate ?? 0}%`} icon={Percent} loading={!kpis} testid="kpi-conversion" />
        <Kpi label="Abbandonati" value={kpis?.abandoned ?? 0} icon={PackageX} loading={!kpis} testid="kpi-abandoned" />
      </div>

      {/* Tabs + toolbar */}
      <div className="flex flex-col gap-3">
        <Tabs value={tab} onValueChange={onTab} data-testid="admin-orders-tabs">
          <TabsList>
            <TabsTrigger value="orders" data-testid="orders-tab-orders">Ordini</TabsTrigger>
            <TabsTrigger value="abandoned" data-testid="orders-tab-abandoned">Carrelli abbandonati</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per numero, email o nome"
              className="bg-surface2 pl-9"
              data-testid="admin-orders-search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full bg-surface2 sm:w-48" data-testid="admin-orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Barra azione spedizioni multiple (solo scheda Ordini) */}
      {tab === "orders" && selectedIds.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-lime/40 bg-lime/10 px-4 py-3"
          data-testid="orders-bulk-bar"
        >
          <p className="text-sm text-chalk">
            {selectedIds.length} {selectedIds.length === 1 ? "ordine selezionato" : "ordini selezionati"}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])} data-testid="orders-bulk-clear">
              Deseleziona
            </Button>
            <Button variant="lime" size="sm" disabled={bulkRunning} onClick={runBulk} data-testid="orders-bulk-ship">
              <Truck className="h-4 w-4" /> {bulkRunning ? "Creazione…" : `Crea spedizioni GLS (${selectedIds.length})`}
            </Button>
          </div>
        </div>
      )}

      {/* Tabella */}
      <Card className="border-line bg-surface2">
        <CardContent className="p-0">
          {error ? (
            <div className="flex items-center justify-between p-4">
              <p className="text-sm text-chalk">{error}</p>
              <Button variant="secondary" size="sm" onClick={loadList}>Riprova</Button>
            </div>
          ) : !list ? (
            <div className="flex flex-col gap-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-11 rounded-lg" />)}
            </div>
          ) : list.items.length === 0 ? (
            <div className="flex flex-col items-start gap-3 p-6">
              <p className="text-sm text-steel">Nessun risultato per i filtri selezionati.</p>
              <Button variant="secondary" size="sm" onClick={() => { setQ(""); setStatus("all"); }} data-testid="orders-reset-filters">
                Reset filtri
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-orders-table">
                <TableHeader>
                  <TableRow className="border-line hover:bg-transparent">
                    {tab === "orders" && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Seleziona tutti gli ordini pagati"
                          disabled={paidRows.length === 0}
                          data-testid="orders-select-all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase tracking-wide text-steel">Ordine</TableHead>
                    <TableHead className="hidden text-xs uppercase tracking-wide text-steel sm:table-cell">Data</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-steel">Cliente</TableHead>
                    <TableHead className="text-right text-xs uppercase tracking-wide text-steel">Totale</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-steel">Pagamento</TableHead>
                    {tab === "orders" && (
                      <TableHead className="hidden text-xs uppercase tracking-wide text-steel md:table-cell">Evasione</TableHead>
                    )}
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.items.map((o) => (
                    <TableRow
                      key={o.order_number}
                      className="cursor-pointer border-line hover:bg-surface3"
                      onClick={() => setSelected(o.order_number)}
                      data-testid="admin-orders-row"
                    >
                      {tab === "orders" && (
                        <TableCell className="py-2.5" onClick={(e) => e.stopPropagation()}>
                          {o.payment_status === "paid" ? (
                            <Checkbox
                              checked={selectedIds.includes(o.order_number)}
                              onCheckedChange={() => toggleOne(o.order_number)}
                              aria-label={`Seleziona ordine ${o.order_number}`}
                              data-testid="orders-row-checkbox"
                            />
                          ) : (
                            <span className="block h-4 w-4" />
                          )}
                        </TableCell>
                      )}
                      <TableCell className="py-2.5">
                        <span className="text-sm font-medium text-chalk">{o.order_number}</span>
                        <span className="block text-xs text-steel sm:hidden">{fmtDate(o.created_at)}</span>
                      </TableCell>
                      <TableCell className="hidden py-2.5 text-sm text-steel sm:table-cell">{fmtDate(o.created_at)}</TableCell>
                      <TableCell className="py-2.5">
                        <span className="block max-w-[180px] truncate text-sm text-chalk">{o.customer_name || "—"}</span>
                        <span className="block max-w-[180px] truncate text-xs text-steel">{o.customer_email}</span>
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-sm tabular-nums text-chalk">{eur(o.total)}</TableCell>
                      <TableCell className="py-2.5"><Pill map={PAY} value={o.payment_status} /></TableCell>
                      {tab === "orders" && (
                        <TableCell className="hidden py-2.5 md:table-cell"><Pill map={FUL} value={o.fulfillment_status} /></TableCell>
                      )}
                      <TableCell className="py-2.5 text-right"><ChevronRight className="h-4 w-4 text-tertiary" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {list && list.items.length > 0 && (
        <p className="text-xs text-steel">{list.total} {list.total === 1 ? "risultato" : "risultati"}</p>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && (
          <OrderDetail
            orderNumber={selected}
            onClose={() => setSelected(null)}
            onChanged={refreshAll}
          />
        )}
      </Sheet>

      {/* Esiti creazione spedizioni multiple */}
      <Dialog open={!!bulkResults} onOpenChange={(o) => { if (!o) { setBulkResults(null); setCombinedPdf(null); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-results-dialog">
          <DialogHeader>
            <DialogTitle>Esito spedizioni GLS</DialogTitle>
            <DialogDescription>
              {bulkResults ? `${bulkResults.ok_count} create su ${bulkResults.total} ordini.` : ""}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh]">
            <div className="flex flex-col gap-2 pr-3">
              {(bulkResults?.results || []).map((r) => (
                <div
                  key={r.order_number}
                  className="flex items-start justify-between gap-3 rounded-lg border border-line bg-surface2 px-3 py-2"
                  data-testid="bulk-result-row"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-chalk">{r.order_number}</p>
                    {r.ok ? (
                      <p className="text-xs text-lime">Spedito · {r.tracking_number}</p>
                    ) : (
                      <p className="text-xs text-steel">{r.error}</p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                      r.ok ? "bg-lime/15 text-lime" : "bg-destructive/15 text-destructive"
                    }`}
                  >
                    {r.ok ? "OK" : "Errore"}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            {combinedPdf && (
              <Button
                variant="lime"
                onClick={() => downloadPdf(combinedPdf, "etichette-gls.pdf")}
                data-testid="bulk-download-combined"
              >
                <Download className="h-4 w-4" /> Scarica PDF unico etichette
              </Button>
            )}
            <Button variant="secondary" onClick={() => { setBulkResults(null); setCombinedPdf(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
